import * as React from "react";
import { cn } from "../../lib/utils";

/**
 * Container-driven layout. Cards reflow on the width of the BOX THEY ARE IN,
 * never on the width of the window.
 *
 * 🔴 THE DEFECT CLASS THIS RETIRES. A record body written with viewport
 * breakpoints (`lg:grid-cols-3`) is correct exactly once — on the page it was
 * authored against. Render the same body inside a drawer, a split pane or a
 * modal and the viewport is still `lg`, so the layout keeps a page-width grid
 * inside a box that is nowhere near page width: the first column clips, the
 * label track eats the value track, and the drawer grows a horizontal
 * scrollbar. The bug is not in any one body — it is in asking the window a
 * question only the container can answer.
 *
 * HOW IT IS DONE, AND WHY NOT CONTAINER QUERIES. `grid-template-columns:
 * repeat(auto-fit, minmax(min(100%, max(<min>, (100% - (N-1)·<gap>) / N)), 1fr))`
 * is container-driven BY CONSTRUCTION: `100%` is the grid's own inline size, so
 * `auto-fit` lays down as many tracks as that width affords and no more. The
 * `max(...)` half caps the count at N — without it a very wide container would
 * keep adding columns past the design.
 *
 * 🔴 THE `min(100%, …)` IS AN OVERFLOW GUARD AND IT IS NOT OPTIONAL. `max()`
 * alone is a HARD floor: a track never goes below `<min>`, so a container
 * NARROWER than `<min>` gets a track wider than itself and the page grows a
 * horizontal scrollbar. `StatsBar` never showed it because its 9.5rem floor is
 * under every real container; a record body at 22rem+ is not. Measured on a
 * 375px phone, whose content box is 327px: a 352px floor overflowed the
 * document by 1px, on the very viewport this layout is meant to rescue.
 * `min(100%, …)` lets the floor give way once — and only once — the container
 * is narrower than a single card, which is exactly when one column is the only
 * honest answer anyway.
 *
 * A CSS `@container` query would express the same intent, and Tailwind 3.4 can
 * emit one through an arbitrary variant. It is not used here because it needs a
 * `container-type` ancestor to be correct (one missing declaration silently
 * collapses every child to one column) and because it is unsupported before
 * Safari 16 / Firefox 110, where the fallback is also one column. `auto-fit` is
 * supported everywhere this app runs and degrades to *more* columns, never
 * fewer, so nothing can silently collapse.
 *
 * Worked example, `cols={3}` with the default 20rem minimum and a 24px gap:
 *
 *   container 1200px (page)   → (1200-48)/3 = 384 ≥ 320 → three columns
 *   container  976px (drawer) → ( 976-48)/3 = 309 <  320 → 320px tracks → two
 *   container  600px (narrow) → one column
 *
 * 🔴 THE MINIMUM IS NOT A DETAIL AT HIGH COLUMN COUNTS. `minColWidth` defaults
 * to 20rem, which suits a two- or three-column record body and is far too wide
 * for a dense counter strip: six columns at 20rem need 6·320 + 5·24 = 2040px of
 * container, roughly twice the page. A caller who asks for `cols={6}` and
 * leaves the default gets THREE — silently, at every width, with nothing in the
 * console. Work the arithmetic (`cols·min + (cols-1)·gap ≤ container`) for the
 * narrowest container the layout must still hold, and pass the result.
 *
 * WHAT THE CALLER STILL OWES: `min-w-0` on any child that can hold an
 * unbreakable value. A grid track is `minmax(…, 1fr)` and a flex/grid item
 * defaults to `min-width: auto`, i.e. never narrower than its content — so a
 * long unbroken string inside a child can still push its own track wide. The
 * grid itself sets `min-w-0` on its root, which is what stops it forcing ITS
 * parent wide.
 */

export type ResponsiveGridColumns = 2 | 3 | 4 | 5 | 6 | 7;

/**
 * One literal per column count — Tailwind only emits class names it can find
 * verbatim in the source, so these can never be interpolated. The gap and the
 * per-column minimum come in as custom properties, which keeps this table at
 * five rows instead of one row per (columns × gap × minimum) combination.
 *
 * The formula is identical in every row and the only thing that varies is the
 * count: `(100% - (N-1)·gap) / N`. 5 and 6 exist so a counter strip can reflow
 * on its container like everything else — see `StatsBar`, which is the caller
 * that needed them and which supplies its own minimum accordingly.
 */
const TEMPLATE: Record<ResponsiveGridColumns, string> = {
  2: "grid-cols-[repeat(auto-fit,minmax(min(100%,max(var(--phx-col-min),(100%_-_1_*_var(--phx-gap))/2)),1fr))]",
  3: "grid-cols-[repeat(auto-fit,minmax(min(100%,max(var(--phx-col-min),(100%_-_2_*_var(--phx-gap))/3)),1fr))]",
  4: "grid-cols-[repeat(auto-fit,minmax(min(100%,max(var(--phx-col-min),(100%_-_3_*_var(--phx-gap))/4)),1fr))]",
  5: "grid-cols-[repeat(auto-fit,minmax(min(100%,max(var(--phx-col-min),(100%_-_4_*_var(--phx-gap))/5)),1fr))]",
  6: "grid-cols-[repeat(auto-fit,minmax(min(100%,max(var(--phx-col-min),(100%_-_5_*_var(--phx-gap))/6)),1fr))]",
  // 7 exists for the loan vitals strip, which carries seven vitals and had
  // been a hand-rolled `grid-cols-2 md:grid-cols-4 xl:grid-cols-7`. One more
  // literal, because Tailwind only emits class names it can see verbatim.
  7: "grid-cols-[repeat(auto-fit,minmax(min(100%,max(var(--phx-col-min),(100%_-_6_*_var(--phx-gap))/7)),1fr))]",
};

/**
 * 🔴 THE NARROWEST A RECORD CARD MAY GET BEFORE AN OVERVIEW SHEDS A COLUMN.
 *
 * Sibling of `StatsBar`'s `STAT_TILE_MIN_WIDTH`, and derived the same way —
 * from what the card has to HOLD, not from a look.
 *
 * A card's field row stacks below a **22rem list width** (`DetailField`), and
 * that list is the card's CONTENT box — so the card itself has to be 22rem plus
 * its own furniture: `p-space-4` either side (48px) and a 1px border either side
 * (2px). 352 + 50 = **402px**, and 26rem (416px) is the smallest round value
 * above it, with 14px of slack.
 *
 * 🔴 22rem WAS THE FIRST ANSWER AND IT IS OFF BY THE CARD'S OWN PADDING, WHICH
 * IS WORSE THAN IT SOUNDS: it does not merely make cards a little tight, it
 * produces the ONE state this layout exists to prevent — two cards across, each
 * too narrow for its own rows, so every label sits on its own line above its
 * value while the page still reads as a two-column grid. Measured at a 1512px
 * window: main 807.73px, two cards of 387.88px, card content 337.88px, which is
 * BELOW the 352px stack threshold. At 26rem the same window gives one card of
 * 807.73px with 757.73px of content, and the rows stay side by side. A card is
 * now either wide enough for its own contents or the only card in the row.
 *
 * Where a two-across body sheds to one, at `2·416 + 32`: **864px**. Measured
 * against the content box `AppShell` now affords, a 1900px window gives a
 * 1536px page, a main column of 1151.39px, and two cards of 559.7px.
 */
export const RECORD_CARD_MIN_WIDTH = "26rem";

/**
 * 🔴 THE NARROWEST A RECORD OVERVIEW'S RIGHT RAIL MAY BE, and it is NOT the
 * card minimum — it is derived from what the rail actually holds.
 *
 * Measured against the real `MilestoneList` with the buyer Overview's own eight
 * rungs, which is the densest rail card in the module (a label and a relative
 * date on one line, per rung): at 344px "Prequalification completed" and its
 * stamp wrap to two lines; at 348px nothing wraps. 22rem (352px) is the
 * smallest round value above the measured requirement.
 *
 * The first specification for this was 20rem, which is 28px short of it.
 */
export const RECORD_RAIL_WIDTH = "22rem";

/** Design-scale gaps, named so a caller never has to keep the formula and the
 *  gap in sync by hand — the same value feeds both. */
export type ResponsiveGap = "sm" | "md" | "lg";

const GAP_PX: Record<ResponsiveGap, string> = {
  sm: "16px",
  md: "24px",
  lg: "32px",
};

export interface ResponsiveGridProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, "children"> {
  /** The most columns this layout ever wants. Fewer are used automatically
   *  whenever the container cannot give each one `minColWidth`. */
  cols?: ResponsiveGridColumns;
  /** The narrowest a column may get before one is dropped. Raise it for
   *  layouts with a fixed label track; lower it for dense chips. */
  minColWidth?: string;
  gap?: ResponsiveGap;
  children: React.ReactNode;
}

export function ResponsiveGrid({
  cols = 3,
  minColWidth = "20rem",
  gap = "md",
  className,
  style,
  children,
  ...props
}: ResponsiveGridProps) {
  return (
    <div
      className={cn(
        "grid min-w-0 items-start gap-[var(--phx-gap)]",
        TEMPLATE[cols],
        className,
      )}
      style={
        {
          "--phx-gap": GAP_PX[gap],
          "--phx-col-min": minColWidth,
          ...style,
        } as React.CSSProperties
      }
      data-phx-grid-cols={cols}
      {...props}
    >
      {children}
    </div>
  );
}

export interface ResponsiveSplitProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, "children"> {
  /** The body. Takes the larger share and keeps it while both fit. */
  main: React.ReactNode;
  /** The side rail. Drops BELOW the main column — full width — as soon as the
   *  container cannot seat both at their natural minimums. */
  rail: React.ReactNode;
  /** Narrowest useful main column before the rail wraps under it. */
  mainMinWidth?: string;
  /** Natural rail width. See {@link ResponsiveSplitProps.railGrows} for whether
   *  it stays at this width or grows past it. */
  railWidth?: string;
  /**
   * Whether the rail takes a share of leftover width, or holds `railWidth`.
   *
   * <p>🔴 <b>Default `true` is this component's original behaviour, and the
   * first thing that ever rendered it wanted the opposite.</b> `ResponsiveSplit`
   * shipped with zero call sites and stayed that way for three weeks, so its
   * split of leftover space had never been seen. Measured on first real use, at
   * the 1536px content box the shell now affords and with the arguments the
   * record Overviews were specified with (`mainMinWidth="34rem"`,
   * `railWidth="20rem"`, `gap="lg"`):</p>
   *
   * <pre>
   *   container 1536  ->  main  970.67   rail  533.33
   *   container 1380  ->  main  866.67   rail  481.33
   *   container 1136  ->  main  704.00   rail  400.00
   * </pre>
   *
   * <p>`grow-[2]` against `grow` divides the leftover 2:1, so `railWidth` is a
   * STARTING width and not a width — exactly as this file documented, and 213px
   * away from the 1184 / 320 the Overviews were sized around. A rail holding a
   * milestone ladder does not want half the leftover space on a wide monitor;
   * the main column does.</p>
   *
   * <p>Pass `false` and the rail holds `railWidth` while both fit, with the main
   * column taking every spare pixel. It still WRAPS below the main column when
   * the two no longer fit — that behaviour is the whole point of the component
   * and is unchanged either way.</p>
   *
   * <p>🔴 IT IS A LOPSIDED RATIO, NOT `grow-0`, AND THE DIFFERENCE ONLY SHOWS
   * ONCE THE RAIL HAS WRAPPED. `grow-0` holds the rail at `railWidth` beside the
   * main column — correct — and then keeps holding it there BELOW the main
   * column too, where it is the only item on its line and this file's own
   * contract says it goes full width. Measured: a 900px container wrapped the
   * rail and left it 352px in a 900px row. Weighting the main column instead
   * (`grow-[999]` against the rail's `grow`) gives the rail 1/1000th of the
   * leftover while they are side by side — 352.6px against a stated 352 at a
   * 1536px container, i.e. sub-pixel — and full width the moment it wraps,
   * because it is then alone on its line with a non-zero grow factor.</p>
   */
  railGrows?: boolean;
  gap?: ResponsiveGap;
}

/**
 * The main-column-plus-rail shape, container-driven.
 *
 * This is `flex-wrap` rather than a grid on purpose: wrapping is decided by the
 * items' own bases against the container's inline size, which is precisely the
 * question being asked. A `grid-cols-3` + `col-span-2` version of the same
 * layout has to be TOLD when to collapse, and the only thing available to tell
 * it with is a viewport breakpoint — the defect above.
 */
export function ResponsiveSplit({
  main,
  rail,
  mainMinWidth = "34rem",
  railWidth = "20rem",
  railGrows = true,
  gap = "md",
  className,
  style,
  ...props
}: ResponsiveSplitProps) {
  return (
    <div
      className={cn("flex min-w-0 flex-wrap items-start gap-[var(--phx-gap)]", className)}
      style={{ "--phx-gap": GAP_PX[gap], ...style } as React.CSSProperties}
      data-phx-split=""
      {...props}
    >
      {/* `grow-[2]` against the rail's `grow` splits leftover width 2:1, so the
          main column widens faster than the rail as the container grows.
          `grow-[999]` is the `railGrows={false}` case — see that prop. */}
      <div
        className={cn(
          "min-w-0 basis-[var(--phx-main-min)]",
          railGrows ? "grow-[2]" : "grow-[999]",
        )}
        style={{ "--phx-main-min": mainMinWidth } as React.CSSProperties}
        data-phx-split-main=""
      >
        {main}
      </div>
      <div
        className="min-w-0 grow basis-[var(--phx-rail-w)]"
        style={{ "--phx-rail-w": railWidth } as React.CSSProperties}
        data-phx-split-rail=""
        data-phx-rail-grows={railGrows ? "" : undefined}
      >
        {rail}
      </div>
    </div>
  );
}
